import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ImagePlus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { useUserData } from '../UserDataContext';
import { businessTokens } from './businessTheme';
import { BusinessService } from '../../services/BusinessService';
import {
  BusinessRegistration,
  PORTFOLIO_IMAGE_COUNT, BUSINESS_NAME_MAX_LENGTH, BUSINESS_DESCRIPTION_MAX_LENGTH,
  normalizeShopLink,
} from '../../types/business';

/**
 * The profile builder — unlocked by payment, not by approval.
 *
 * The pitch text is NOT carried across. It argued that the business belongs
 * here; it is not profile copy, and publishing it as one would put a sales
 * pitch aimed at moderators in front of customers.
 *
 * The name and shop link ARE carried across, prefilled and still editable.
 * Nobody should type their own business name twice, and a business that moved
 * from a Linktree to a real domain between applying and paying should not have
 * to reapply. When the link differs from what was reviewed the owner is told so
 * out loud rather than silently.
 */
export function BusinessProfileBuilderScreen({ onBack }: { onBack: () => void }) {
  const { theme } = useTheme();
  const { userProfile } = useUserData();
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  const t = businessTokens(theme);

  const ownerId = String(userProfile?.id || '');

  const [registration, setRegistration] = useState<BusinessRegistration | null>(null);
  const [loading, setLoading] = useState(true);
  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [shopLink, setShopLink] = useState('');
  const [images, setImages] = useState<(string | null)[]>(Array(PORTFOLIO_IMAGE_COUNT).fill(null));
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSlotRef = useRef<number>(0);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!ownerId || !registrationId) return;
      const regs = await BusinessService.getMyRegistrations(ownerId);
      if (!active) return;
      const reg = regs.find(r => r.id === registrationId) || null;
      setRegistration(reg);
      // Both prefilled from the application, both still editable. Nobody should
      // type their own business name twice, and a business that moved domains
      // between applying and paying should not have to reapply.
      if (reg) {
        setBusinessName(reg.businessName || '');
        setShopLink(reg.shopLink || '');
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [ownerId, registrationId]);

  const handlePickImage = (slot: number) => {
    pendingSlotRef.current = slot;
    fileInputRef.current?.click();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-picking the same file still fires a change event.
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image.'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('That image is over 8MB. Pick a smaller one.'); return; }

    const slot = pendingSlotRef.current;
    setUploadingSlot(slot);
    try {
      const url = await BusinessService.uploadBusinessMedia(ownerId, file, 'portfolio');
      setImages(prev => { const next = [...prev]; next[slot] = url; return next; });
    } catch (err: any) {
      console.error('[BusinessBuilder] upload failed', err);
      toast.error('That image would not upload. Try again.');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleSubmit = async () => {
    if (submitting || !registrationId) return;
    setSubmitting(true);
    const res = await BusinessService.createProfile(ownerId, registrationId, {
      businessName,
      description,
      shopLink,
      portfolioImages: images.filter(Boolean) as string[],
    });
    setSubmitting(false);
    if (!res.success || !res.data) { toast.error(res.error || 'Could not create your profile.'); return; }
    toast.success('Your business profile is live.');
    navigate(`/business/${res.data.profileId}`, { replace: true });
  };

  const linkChanged = !!registration
    && normalizeShopLink(shopLink) !== normalizeShopLink(registration.shopLink || '');
  const filledImages = images.filter(Boolean).length;
  const ready = businessName.trim().length >= 2
    && shopLink.trim().length > 0
    && filledImages === PORTFOLIO_IMAGE_COUNT;

  const inputStyle: React.CSSProperties = {
    width: '100%', background: t.inset, border: `1px solid ${t.border}`,
    borderRadius: 12, padding: '12px 14px', color: t.text, fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6,
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  // The same three conditions the create rule checks, stated as screens rather
  // than as a rejected write.
  const blocked = !loading && (
    !registration ? 'That application could not be found.'
      : registration.status !== 'approved' ? 'That application has not been approved yet.'
      : !registration.paidAt ? 'Payment has not cleared yet. It usually takes a few seconds.'
      : registration.businessProfileId ? 'You have already built this profile.'
      : null
  );

  return (
    <div style={{ width: '100%', height: '100vh', background: t.bg, overflowY: 'auto' }}>
      <div
        style={{
          position: 'sticky', top: 0, zIndex: 5, background: t.bg,
          borderBottom: `1px solid ${t.border}`, padding: '0 16px 16px',
          paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            aria-label="Go back"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text, padding: 0 }}
          >
            <ArrowLeft style={{ width: 24, height: 24 }} />
          </motion.button>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: t.text }}>Build your profile</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: t.textMuted }}>
              This is what customers see
            </p>
          </div>
        </div>
      </div>

      <div style={{ padding: '18px 16px 120px', maxWidth: 640, margin: '0 auto' }}>
        {loading ? (
          <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
            <Loader2 style={{ width: 28, height: 28, color: t.accent }} className="animate-spin" />
          </div>
        ) : blocked ? (
          <div
            style={{
              background: t.surface, border: `1px solid ${t.border}`,
              borderRadius: 18, padding: 24, textAlign: 'center',
            }}
          >
            <AlertTriangle style={{ width: 30, height: 30, color: t.warning, margin: '0 auto 10px' }} />
            <p style={{ margin: 0, fontSize: 15, color: t.text }}>{blocked}</p>
            <button
              type="button"
              onClick={onBack}
              style={{
                marginTop: 16, height: 42, padding: '0 20px', borderRadius: 12, border: 'none',
                background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Back to My Business
            </button>
          </div>
        ) : (
          <>
            <label style={labelStyle}>Business name</label>
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value.slice(0, BUSINESS_NAME_MAX_LENGTH))}
              placeholder="What your business is called"
              style={inputStyle}
            />
            <p style={{ margin: '6px 0 18px', fontSize: 11, color: t.textMuted }}>
              Taken from your application. Change it now if you want, but changing it once
              you are live goes back through a quick review.
            </p>

            <label style={labelStyle}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, BUSINESS_DESCRIPTION_MAX_LENGTH))}
              placeholder="What you make or sell, in a couple of sentences."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
            />
            <p style={{ margin: '4px 0 18px', fontSize: 11, color: t.textMuted, textAlign: 'right' }}>
              {description.length}/{BUSINESS_DESCRIPTION_MAX_LENGTH}
            </p>

            <label style={labelStyle}>Shop link</label>
            <input
              value={shopLink}
              onChange={(e) => setShopLink(e.target.value)}
              placeholder="yourshop.com"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              style={inputStyle}
            />
            {linkChanged ? (
              <div
                style={{
                  marginTop: 8, marginBottom: 18, padding: 10, borderRadius: 10,
                  background: 'rgba(243,156,18,0.12)', border: `1px solid ${t.warning}44`,
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}
              >
                <AlertTriangle style={{ width: 15, height: 15, color: t.warning, flexShrink: 0, marginTop: 1 }} />
                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: t.text }}>
                  This is not the link that was reviewed ({registration?.shopLink}). That is
                  allowed, but a moderator may check it.
                </p>
              </div>
            ) : (
              <p style={{ margin: '6px 0 18px', fontSize: 11, color: t.textMuted }}>
                Taken from your application. Change it if it has moved.
              </p>
            )}

            <label style={labelStyle}>
              Portfolio ({filledImages}/{PORTFOLIO_IMAGE_COUNT})
            </label>
            <p style={{ margin: '0 0 10px', fontSize: 11, color: t.textMuted }}>
              Four images of your work or products. No captions, no prices: those live in your
              store.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              {images.map((url, slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => handlePickImage(slot)}
                  aria-label={`Portfolio image ${slot + 1}`}
                  style={{
                    position: 'relative', aspectRatio: '1 / 1', width: '100%',
                    borderRadius: 14, overflow: 'hidden', cursor: 'pointer', padding: 0,
                    background: url ? 'transparent' : t.inset,
                    border: `1px ${url ? 'solid' : 'dashed'} ${url ? t.border : t.accent}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {uploadingSlot === slot && (
                    <div
                      style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Loader2 style={{ width: 22, height: 22, color: '#fff' }} className="animate-spin" />
                    </div>
                  )}
                  {!url && uploadingSlot !== slot && (
                    <ImagePlus style={{ width: 24, height: 24, color: t.accent }} />
                  )}
                </button>
              ))}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              style={{ display: 'none' }}
            />

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!ready || submitting}
              style={{
                width: '100%', height: 50, borderRadius: 14, border: 'none',
                background: t.buttonBg, color: t.buttonText, fontSize: 15, fontWeight: 700,
                opacity: (!ready || submitting) ? 0.5 : 1,
                cursor: (!ready || submitting) ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {submitting && <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />}
              {submitting ? 'Publishing' : 'Publish my business profile'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
